import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import type { BillingSubscriptionPanel } from '@repo/contracts';
import {
  CREDIT_PACK_DETAILS,
  CreditPackSize,
  FREE_PLAN_CREDITS_LINE,
  FREE_PLAN_FEATURES,
  PRO_MONTHLY_CREDITS,
  PRO_PLAN_CREDITS_LINE,
  PRO_PLAN_FEATURES,
} from '@repo/contracts/billing';
import { Skeleton } from '@repo/ui/skeleton';

import { trackEvent } from '../analytics/posthog';
import { useCredits } from '../credits/use-credits';
import { CheckIcon, ExternalLinkIcon } from '../lib/icons';
import { useBillingBack } from './billing-back';
import {
  formatMoney,
  getSubscriptionBadge,
  getSubscriptionMessage,
  isEndingWithoutRenewal,
} from './billing-format';
import {
  BillingFrame,
  BUY_BUTTON,
  CARD,
  Eyebrow,
  PageTitle,
  PILL_BUTTON,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
  SectionHeader,
  TEXT_BUTTON,
  VIOLET_CHIP,
} from './billing-ui';
import {
  useHandleCheckoutReturn,
  useOpenBillingPortal,
  useStartCreditCheckout,
  useStartSubscription,
} from './use-billing-actions';
import { useBillingOverview } from './use-billing-overview';

const PRO_PRICE = '$10';

const PACKS = [
  CreditPackSize.QUICK,
  CreditPackSize.PACK1,
  CreditPackSize.PACK2,
  CreditPackSize.PACK3,
];

const errorText = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

function ErrorNote({ children }: { children: string }) {
  return (
    <p className="mt-4 rounded-button bg-tag-critical px-4 py-3 text-ui text-critical">
      {children}
    </p>
  );
}

function Features({
  extra,
  items,
}: {
  /** A last line that is more than text — a link, say. */
  extra?: ReactNode;
  items: readonly string[];
}) {
  return (
    <ul className="mt-3 space-y-2">
      {[...items, ...(extra ? [extra] : [])].map((feature, index) => (
        <li
          className="flex items-start gap-2.5 text-ui text-text-secondary"
          key={typeof feature === 'string' ? feature : `extra-${index}`}
        >
          <CheckIcon className="mt-0.5 size-3.5 shrink-0 text-success-ink" />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
  );
}

/** Floto Rooms' account carries the wider Floto product with it — its tools
 *  are sold and used on floto.ai, so the card links there rather than listing
 *  them. */
const FLOTO_AI_FEATURES = (
  <>
    Includes{' '}
    <a
      className="inline-flex items-center gap-1 underline-offset-2 hover:text-text-primary hover:underline"
      href="https://floto.ai"
      rel="noopener noreferrer"
      target="_blank"
    >
      Floto.ai features
      <ExternalLinkIcon className="size-3" />
    </a>
  </>
);

/** One plan, side by side with the other, for a free user choosing. */
function PlanCard({
  children,
  current,
  credits,
  extraFeature,
  features,
  highlighted,
  name,
  per,
  price,
}: {
  children?: ReactNode;
  current: boolean;
  credits: string;
  extraFeature?: ReactNode;
  features: readonly string[];
  /** The plan being offered: a violet edge. */
  highlighted?: boolean;
  name: string;
  per: string;
  price: string;
}) {
  return (
    <div
      className={`flex flex-col rounded-tile border bg-surface p-5 ${
        highlighted ? 'border-room-violet' : 'border-hairline'
      }`}
    >
      <div className="flex items-center gap-2">
        <p className="text-ui-lg font-bold text-text-primary">{name}</p>
        {current && <span className={VIOLET_CHIP}>Current</span>}
      </div>
      <p className="mt-3 font-heading text-3xl font-bold tracking-tight text-text-primary">
        {price}
      </p>
      <p className="text-ui text-text-secondary">{per}</p>
      <p className="mt-1 text-ui-strong font-bold text-text-primary">
        {credits}
      </p>
      <Features extra={extraFeature} items={features} />
      {children ? <div className="mt-auto pt-7">{children}</div> : null}
    </div>
  );
}

function UpgradeButton() {
  const upgrade = useStartSubscription();
  return (
    <>
      <button
        className={`${PRIMARY_BUTTON} w-full`}
        disabled={upgrade.isPending}
        onClick={() => {
          upgrade.mutate({ plan: 'pro' });
        }}
        type="button"
      >
        {upgrade.isPending ? 'Redirecting to Stripe…' : 'Upgrade to Pro'}
      </button>
      {upgrade.isError && (
        <ErrorNote>
          {errorText(upgrade.error, 'Failed to start upgrade')}
        </ErrorNote>
      )}
    </>
  );
}

/** Free: both plans, Pro offered. */
function FreePlans() {
  return (
    <div className="grid gap-6 md:grid-cols-2">
      <PlanCard
        credits={FREE_PLAN_CREDITS_LINE}
        current
        extraFeature={FLOTO_AI_FEATURES}
        features={FREE_PLAN_FEATURES}
        name="Free"
        per="forever"
        price="$0"
      />
      <PlanCard
        credits={PRO_PLAN_CREDITS_LINE}
        current={false}
        features={PRO_PLAN_FEATURES}
        highlighted
        name="Pro"
        per="per month"
        price={PRO_PRICE}
      >
        <UpgradeButton />
      </PlanCard>
    </div>
  );
}

/**
 * Pro: the plan as it stands, and the way into Stripe for everything else —
 * changing or cancelling the plan, the card, invoices. Only says more than
 * "Pro" when something needs attention: a failed payment, a plan that won't
 * renew.
 */
function CurrentPlan({ panel }: { panel: BillingSubscriptionPanel | null }) {
  const portal = useOpenBillingPortal();
  const badge = panel ? getSubscriptionBadge(panel) : null;
  const message = panel ? getSubscriptionMessage(panel) : null;
  const flagged = badge && badge.label !== 'Active' ? badge : null;
  const renews =
    panel && !isEndingWithoutRenewal(panel) && panel.projectedTotal != null
      ? ` · ${formatMoney(panel.projectedTotal, panel.currency)}`
      : '';
  const openPortal = () => {
    portal.mutate(undefined);
  };

  return (
    <div className={`${CARD} flex flex-col p-5`}>
      <Eyebrow>Current plan</Eyebrow>
      <div className="mt-3 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <p className="font-heading text-3xl font-bold tracking-tight text-text-primary">
          Pro
        </p>
        <p className="text-ui text-text-secondary">{PRO_PRICE}</p>
        {flagged && (
          <span
            className={`self-center rounded-pill px-2.5 py-0.5 text-tag font-semibold ${flagged.className}`}
          >
            {flagged.label}
          </span>
        )}
      </div>
      <p className="mt-2 text-ui text-text-secondary">
        {PRO_PLAN_CREDITS_LINE}
      </p>
      {message && (
        <p
          className={`mt-1 text-caption ${
            message.tone === 'warning'
              ? 'font-medium text-critical'
              : 'text-text-muted'
          }`}
        >
          {message.text}
          {renews}
        </p>
      )}
      <div className="mt-auto flex flex-wrap items-center gap-2 pt-5">
        <button
          className={SECONDARY_BUTTON}
          disabled={portal.isPending}
          onClick={openPortal}
          type="button"
        >
          {portal.isPending ? 'Opening…' : 'Change plan'}
        </button>
        <button
          className={TEXT_BUTTON}
          disabled={portal.isPending}
          onClick={openPortal}
          type="button"
        >
          Billing details
        </button>
      </div>
      {portal.isError && (
        <ErrorNote>
          {errorText(portal.error, 'Failed to open billing portal')}
        </ErrorNote>
      )}
    </div>
  );
}

/**
 * What's left to spend, beside "Usage", for a Free account. Pro shows the same
 * in its monthly-credits card; Free has no such card, so without this it
 * showed no balance at all. Nothing
 * until the balance has loaded, rather than a zero that isn't true.
 */
function CreditsLeft() {
  const { balance, loaded } = useCredits();
  if (!loaded) return null;
  return (
    <div className="text-right" role="status">
      <p className="text-tag font-medium tracking-[0.08em] text-text-muted uppercase">
        Credits remaining
      </p>
      <p className="font-heading text-panel-title font-bold text-text-primary tabular-nums">
        {balance.totalCredits.toLocaleString()}
      </p>
    </div>
  );
}

/**
 * What is left of this month's allowance. `baseCredits` is the allowance's
 * remainder (reset on renewal); bought credits sit apart in `topupCredits`
 * and are named on their own line, so a pack just bought doesn't look lost.
 */
function MonthlyCredits() {
  const { balance, loaded } = useCredits();

  if (!loaded) {
    return <Skeleton className="h-full min-h-40 rounded-tile" />;
  }

  const left = Math.min(Math.max(balance.baseCredits, 0), PRO_MONTHLY_CREDITS);
  const used = PRO_MONTHLY_CREDITS - left;
  const percent = (used / PRO_MONTHLY_CREDITS) * 100;

  return (
    <div className={`${CARD} flex flex-col p-5`}>
      <Eyebrow>Credits</Eyebrow>
      <p className="mt-3 flex items-baseline gap-2">
        <span className="font-heading text-3xl font-bold tracking-tight text-text-primary tabular-nums">
          {left.toLocaleString()}
        </span>
        <span className="text-ui-lg text-text-secondary">left this month</span>
      </p>
      <div
        aria-label="Monthly credits used"
        aria-valuemax={PRO_MONTHLY_CREDITS}
        aria-valuemin={0}
        aria-valuenow={used}
        className="mt-3 h-2 overflow-hidden rounded-pill bg-tag-minor"
        role="progressbar"
      >
        <div
          className="h-full rounded-pill bg-brand transition-[width] duration-500"
          style={{ width: `${percent}%` }}
        />
      </div>
      <p className="mt-3 text-caption text-text-secondary">
        {used.toLocaleString()} of {PRO_MONTHLY_CREDITS.toLocaleString()} used
        this month
        {balance.topupCredits > 0
          ? ` · ${balance.topupCredits.toLocaleString()} top-up credits on top`
          : ''}
      </p>
    </div>
  );
}

/** The four packs, each one click from Stripe. */
function TopUps() {
  const checkout = useStartCreditCheckout();
  const buying = checkout.isPending ? checkout.variables.packSize : null;

  return (
    <section className="space-y-4">
      <SectionHeader>Top up credits</SectionHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {PACKS.map((packSize) => {
          const pack = CREDIT_PACK_DETAILS[packSize];
          return (
            <div className={`${CARD} flex flex-col p-5`} key={packSize}>
              <div className="flex h-6 items-center justify-between gap-3">
                <p className="text-ui-lg font-bold text-text-primary">
                  {pack.label}
                </p>
                {pack.savePercentage && (
                  <span className={VIOLET_CHIP}>
                    Save {pack.savePercentage}
                  </span>
                )}
              </div>
              <p className="mt-3 font-heading text-3xl font-bold tracking-tight text-text-primary">
                {pack.price}
              </p>
              <p className="text-ui-strong font-bold text-text-primary">
                {pack.credits.toLocaleString()} credits
              </p>
              <button
                aria-label={`Buy ${pack.credits.toLocaleString()} credits for ${pack.price}`}
                className={`${BUY_BUTTON} mt-8 w-full`}
                disabled={checkout.isPending}
                onClick={() => {
                  checkout.mutate({ packSize });
                }}
                type="button"
              >
                {buying === packSize ? 'Redirecting to Stripe…' : 'Buy'}
              </button>
            </div>
          );
        })}
      </div>
      {checkout.isError && (
        <ErrorNote>
          {errorText(checkout.error, 'Failed to create checkout session')}
        </ErrorNote>
      )}
    </section>
  );
}

function PageSkeleton() {
  return (
    <div className="space-y-8">
      <div className="grid gap-6 md:grid-cols-2">
        <Skeleton className="h-56 rounded-tile" />
        <Skeleton className="h-56 rounded-tile" />
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Skeleton className="h-44 rounded-tile" />
        <Skeleton className="h-44 rounded-tile" />
        <Skeleton className="h-44 rounded-tile" />
        <Skeleton className="h-44 rounded-tile" />
      </div>
    </div>
  );
}

/**
 * Plans & billing — the plan, and credit top-ups. One page, no dialogs:
 * every button goes straight to Stripe, and everything Stripe already does
 * well (card, cancelling, invoices) is behind its portal.
 *
 * Free sees both plans with Pro offered; Pro sees the plan it has and what is
 * left of the month's credits.
 *
 * Personal billing only: Floto Rooms has no Teams or Enterprise for now (see
 * `tenant/teams-available.ts`).
 */
export function BillingPage() {
  const { overview, isLoading, error } = useBillingOverview();
  const [, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const stripCheckoutParams = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('session_id');
        next.delete('topup');
        return next;
      },
      { replace: true },
    );
  }, [setSearchParams]);

  const handleCheckoutReturn = useHandleCheckoutReturn(stripCheckoutParams);

  useEffect(() => {
    handleCheckoutReturn();
  }, [handleCheckoutReturn]);

  // Once the plan is known: the route alone can't say whether a free user
  // or a Pro one is looking, nor what sent them (a room's Top up, the menu).
  const location = useLocation();
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current || !overview) return;
    viewed.current = true;
    trackEvent('billing_viewed', {
      plan: overview.personalPlanType,
      source: (location.state as { from?: string } | null)?.from ?? 'link',
    });
  }, [location.state, overview]);

  const back = useBillingBack();
  const isPro = overview?.personalPlanType === 'pro';
  const proPanel =
    overview?.subscriptions.find((panel) => panel.kind === 'pro') ?? null;

  return (
    <BillingFrame back={back}>
      <header className="mt-6 mb-9 flex items-start justify-between gap-4">
        <div>
          <PageTitle>Plans &amp; billing</PageTitle>
          <p className="mt-2 text-body text-text-secondary">
            Your plan and credits for the Personal workspace.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-4">
          {/* Pro has the monthly-credits card; Free would otherwise show no
              balance at all. */}
          {isPro ? null : <CreditsLeft />}
          <button
            className={PILL_BUTTON}
            onClick={() => {
              void navigate('/billing/usage', { state: { from: 'billing' } });
            }}
            type="button"
          >
            Usage
          </button>
        </div>
      </header>

      {error ? (
        <div className="rounded-tile border border-critical/30 bg-tag-critical p-6 text-center">
          <p className="text-ui font-semibold text-critical">{error}</p>
          <button
            className={`${PRIMARY_BUTTON} mx-auto mt-4`}
            onClick={() => {
              window.location.reload();
            }}
            type="button"
          >
            Try again
          </button>
        </div>
      ) : isLoading || !overview ? (
        <PageSkeleton />
      ) : (
        <div className="space-y-8">
          {isPro ? (
            <div className="grid gap-4 md:grid-cols-2">
              <CurrentPlan panel={proPanel} />
              <MonthlyCredits />
            </div>
          ) : (
            <FreePlans />
          )}

          <TopUps />

          <p className="pt-4 text-center text-caption text-text-muted">
            Secure checkout by Stripe. Credits arrive right after payment and
            never expire. Charges appear as FLOTO INC on your statement.
          </p>
        </div>
      )}
    </BillingFrame>
  );
}
